import i18next from "i18next";
import { DEFAULT_LOCALE } from "@/lib/constants";

/**
 * The locale every date and time in the app is rendered in.
 *
 * Thirty-nine call sites passed the literal `nl-NL` straight to `toLocaleDateString`, while
 * `DEFAULT_LOCALE` sat in `constants.ts` used by nobody. Today that is harmless — `LANGUAGES` holds
 * one entry and `resources` one bundle, so Dutch is the only language there is. It stops being
 * harmless the moment a second bundle lands: the interface would switch and every date, weekday and
 * month name would stay Dutch, in a product sold white-label to organizations that are not.
 *
 * Reading the language here rather than at module load is what makes it follow `changeLanguage`.
 * Components that call `useTranslation` re-render on that switch and pick the new locale up on the
 * way through; the rest catch up on their next render, which beats never.
 *
 * The import is `i18next` and not `@/i18n` on purpose. Both reach the same singleton, but `@/i18n`
 * _initializes_ it as a side effect of being imported — so importing it here would drag the whole
 * i18n setup into every module that wants to print a date, and into every test that renders one.
 * Reaching for the singleton directly leaves that ordering to the app's entry point, where it
 * belongs. Before it is initialized `resolvedLanguage` is simply undefined, which is exactly when
 * the default should apply anyway.
 */
export function currentLocale(): string {
  // A bare language code is a valid locale — `Intl` resolves "nl" the way it resolves "nl-NL". So no
  // lookup table is needed, and none can fall out of date with the bundles that actually exist.
  return i18next.resolvedLanguage ?? i18next.language ?? DEFAULT_LOCALE;
}
